import React from "react";
import "./GMBScoring.css";
import Tabs, { TabPane } from "../ui/Tabs";
import TopIssues from "./TopIssues";
import ValidCriteria from "./ValidCriteria";
import Essentials from "./FeaturesBlocks/Essentials";
import ProgressCircle from "./ProgressCircle";
import Informations from "./FeaturesBlocks/Informations";
import SocialProfiles from "./FeaturesBlocks/SocialProfiles";
import Reviews from "./FeaturesBlocks/Reviews";
import Engagement from "./FeaturesBlocks/Engagement";

const GMBScoring = ({
  googleInfo,
  displayGMBSection,
  hasFullAccess,
}) => {
  if (!displayGMBSection || !googleInfo) return null;

  const { business } = googleInfo;

  if (business.LocalScore == null) {
    return (
      <div className="container">
        <div className="gmb-error-state">
          <div className="gmb-error-state__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 className="gmb-error-state__title">Données incomplètes</h3>
          <p className="gmb-error-state__text">
            Nous n'avons pas pu récupérer toutes les informations de votre fiche.
            Veuillez réessayer dans quelques instants.
          </p>
          <button
            className="gmb-error-state__btn"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const competitionLabel =
    business.Competition === "High"
      ? "Élevée"
      : business.Competition === "Low"
        ? "Faible"
        : "Moyenne";
  const competitionColor =
    business.Competition === "High"
      ? "red"
      : business.Competition === "Low"
        ? "green"
        : "orange";

  return (
    <div className="container">
      {/* Business header */}
      <div className="gmb-header">
        <div className="gmb-header__top">
          <h2 className="gmb-header__name">
            <a
              href={`https://local.google.com/place?id=${business.Ludocid}&use=srp&hl=fr`}
              target="_blank"
              rel="noreferrer noopener"
              className="gmb-header__link"
            >
              {business.BusinessName}
            </a>
          </h2>
          {business.Industry && (
            <span className="gmb-header__tag">{business.Industry}</span>
          )}
        </div>
        <p className="gmb-header__address">{business.Address}</p>
        <div className="gmb-header__meta">
          {business.Phone && <span>{business.Phone}</span>}
          {business.Phone && business.Website && <span className="gmb-header__sep">|</span>}
          {business.Website && (
            <a href={business.Website} target="_blank" rel="noreferrer noopener" className="gmb-header__website">
              {business.Website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </a>
          )}
        </div>
        {business.Nbreviews > 0 && (
          <p className="gmb-header__reviews">
            Noté <strong>{business.Rating}</strong>/5 sur <strong>{business.Nbreviews}</strong> avis
          </p>
        )}
        <a
          href={`http://search.google.com/local/writereview?placeid=${business.PlaceID}&hl=fr`}
          target="_blank"
          rel="noreferrer noopener"
          className="gmb-header__review-cta"
        >
          Obtenir votre lien direct pour collecter des avis
        </a>
      </div>

      {/* Score cards */}
      <div className="gmb-cards">
        <div className="gmb-card gmb-card--score">
          <div className="gmb-card__circle">
            <ProgressCircle score={business.LocalScore} width={68} type="circle" />
          </div>
          <div className="gmb-card__info">
            <span className="gmb-card__label">Local Score</span>
            <span className="gmb-card__hint">
              {business.LocalScore >= 90 ? "Excellent" : business.LocalScore >= 60 ? "Peut mieux faire" : "Insuffisant"}
            </span>
          </div>
        </div>
        <div className="gmb-card">
          <div className="gmb-card__icon gmb-card__icon--green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <span className="gmb-card__value gmb-card__value--green">
            {googleInfo.valid.length}
          </span>
          <span className="gmb-card__label">Critères valides</span>
        </div>
        <div className="gmb-card">
          <div className="gmb-card__icon gmb-card__icon--red">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <span className="gmb-card__value gmb-card__value--red">
            {googleInfo.issues.length}
          </span>
          <span className="gmb-card__label">Problèmes</span>
        </div>
        <div className="gmb-card">
          <div className={`gmb-card__icon gmb-card__icon--${competitionColor}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <span className={`gmb-card__value gmb-card__value--${competitionColor}`}>
            {competitionLabel}
          </span>
          <span className="gmb-card__label">Concurrence</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="gmb-tabs">
        <Tabs defaultActiveKey="0">
          <TabPane tab="Vue d'ensemble" tabKey="0">
            <div className="gmb-tab-content">
              <h3 className="gmb-tab-content__title">Détail de l'audit</h3>
              <TopIssues hasFullAccess={hasFullAccess} issues={googleInfo.issues} />
              <ValidCriteria hasFullAccess={hasFullAccess} valid={googleInfo.valid} />
            </div>
          </TabPane>
          <TabPane tab="Indispensables" tabKey="1">
            <div className="gmb-tab-content">
              <Essentials auditInfo={business} />
            </div>
          </TabPane>
          <TabPane tab="Informations" tabKey="3" disabled={!hasFullAccess}>
            <div className="gmb-tab-content">
              <Informations auditInfo={business} />
            </div>
          </TabPane>
          <TabPane tab="Réseaux sociaux" tabKey="4" disabled={!hasFullAccess}>
            <div className="gmb-tab-content">
              <SocialProfiles auditInfo={business} />
            </div>
          </TabPane>
          <TabPane tab="Avis" tabKey="5" disabled={!hasFullAccess}>
            <div className="gmb-tab-content">
              <Reviews auditInfo={business} />
            </div>
          </TabPane>
          <TabPane tab="Engagement" tabKey="6" disabled={!hasFullAccess}>
            <div className="gmb-tab-content">
              <Engagement auditInfo={business} />
            </div>
          </TabPane>
        </Tabs>
      </div>

      {/* CTA */}
      <div className="gmb-cta">
        <div className="gmb-cta__inner">
          <span className="gmb-cta__badge">Localranker</span>
          <p className="gmb-cta__title">
            {hasFullAccess
              ? "Devenez le meilleur établissement de votre secteur"
              : "Débloquez l'audit complet de votre fiche GBP"}
          </p>
          <p className="gmb-cta__desc">
            {hasFullAccess
              ? "Découvrez Localranker, la plateforme de gestion et d'optimisation Google Business Profile."
              : "Points bloquants, lien d'avis, analyse concurrentielle locale et recommandations personnalisées."}
          </p>
          <a
            href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=audit-google-my-business"
            target="_blank"
            rel="noreferrer noopener"
            className="gmb-cta__btn"
          >
            Demander une démo gratuite
          </a>
        </div>
      </div>
    </div>
  );
};

export default GMBScoring;
